/**
 * Table Styles Configuration
 * Centralized styles for all DataSection tables (Nakshatra Anga, Chara Karaka, Ashtamsha, Lakshmi)
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * 🎨 CHAKRA UI COLOR TOKENS REFERENCE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Format: 'colorName.shade' where shade = 50 (lightest) to 900 (darkest)
 *
 * 📝 AVAILABLE COLORS:
 * ────────────────────
 * Neutrals:
 *   • gray         - Gray scale (white → black)
 *   • blackAlpha   - Black with transparency
 *   • whiteAlpha   - White with transparency
 *
 * Primary Palette:
 *   • red          - Red tones
 *   • orange       - Orange tones
 *   • yellow       - Yellow tones
 *   • green        - Green tones
 *   • teal         - Teal/Cyan-green tones
 *   • blue         - Blue tones
 *   • cyan         - Cyan/Light blue tones
 *   • purple       - Purple/Violet tones
 *   • pink         - Pink/Magenta tones
 *
 * 🎯 SHADE NUMBERS (all colors have these):
 * ─────────────────────────────────────────
 *   50  → Lightest (almost white with tint)      - Best for: hover, backgrounds
 *   100 → Very light                              - Best for: hover, selected states
 *   200 → Light                                   - Best for: borders, dividers
 *   300 → Light-medium                            - Best for: disabled states
 *   400 → Medium-light                            - Best for: placeholder text
 *   500 → Base/Standard (most vibrant)            - Best for: buttons, badges, icons
 *   600 → Medium-dark                             - Best for: text, emphasis
 *   700 → Dark                                    - Best for: headings, strong text
 *   800 → Very dark                               - Best for: headers, high contrast
 *   900 → Darkest                                 - Best for: primary text, high emphasis
 *
 * 💡 QUICK EXAMPLES:
 * ──────────────────
 * Hover effects (use 50-100):
 *   bg: 'gray.50'     - Neutral gray
 *   bg: 'blue.50'     - Cool blue
 *   bg: 'purple.50'   - Soft purple
 *   bg: 'teal.50'     - Fresh teal
 *   bg: 'green.50'    - Positive green
 *   bg: 'yellow.50'   - Warm yellow
 *   bg: 'pink.50'     - Gentle pink
 *
 * Text colors (use 600-800):
 *   color: 'gray.800'    - Primary text
 *   color: 'gray.600'    - Secondary text
 *   color: 'blue.600'    - Links
 *   color: 'red.600'     - Errors
 *   color: 'green.600'   - Success
 *   color: 'orange.600'  - Warnings
 *
 * Borders (use 200-300):
 *   borderColor: 'gray.200'   - Light border
 *   borderColor: 'gray.300'   - Standard border
 *   borderColor: 'blue.200'   - Accent border
 *
 * Backgrounds (use 50-100):
 *   bg: 'gray.50'      - App background
 *   bg: 'gray.100'     - Section background
 *   bg: 'white'        - Card background
 *
 * 🔧 VS CODE TIP: Type 'bg: "' then Ctrl+Space to see all colors with autocomplete!
 * ═══════════════════════════════════════════════════════════════════════════════
 */

/**
 * Outer Box wrapper styles
 */
export const TABLE_WRAPPER_STYLES = {
    mt: 1,
    mb: 4,
    ml: 0,
    maxW: '100%',
    overflowX: 'auto'
} as const;

/**
 * TableContainer styles (with border and scroll)
 * Used by all 4 tables: NakshatraAnga, CharaKaraka, Ashtamsha, Lakshmi
 */
export const TABLE_CONTAINER_STYLES = {
    overflowX: 'auto' as const,
    border: '1px solid',
    borderColor: 'gray.200',
    borderRadius: 'md',
    maxW: '100%'
} as const;

/**
 * Table base styles
 */
export const TABLE_BASE_STYLES = {
    size: 'sm' as const,
    variant: 'simple' as const,
    minW: '600px'
} as const;

/**
 * Table header (Thead) styles
 *
 * 🎨 Header background options:
 * bg: 'gray.50'   // Light gray (current) ✓
 * bg: 'gray.100'  // Medium gray
 * bg: 'blue.50'   // Light blue header
 * bg: 'purple.50' // Light purple header
 */
export const TABLE_HEADER_STYLES = {
    bg: 'gray.50'
} as const;

/**
 * Alternative header style (for some tables)
 *
 * 🎨 Alt header background options:
 * bg: 'gray.100'  // Medium gray (current) ✓
 * bg: 'gray.200'  // Darker gray
 * bg: 'blue.100'  // Stronger blue
 */
export const TABLE_HEADER_ALT_STYLES = {
    bg: 'gray.100'
} as const;

/**
 * Table row hover effect
 *
 * 🎨 Quick color picker (uncomment to use):
 * ─────────────────────────────────────────
 * bg: 'gray.50'      // Neutral (safe default)
 * bg: 'blue.50'      // Cool blue (professional)
 * bg: 'purple.50'    // Soft purple (current) ✓
 * bg: 'teal.50'      // Fresh teal (modern)
 * bg: 'green.50'     // Positive green (success vibe)
 * bg: 'yellow.50'    // Warm yellow (attention)
 * bg: 'pink.50'      // Gentle pink (soft)
 * bg: 'orange.50'    // Warm orange (energetic)
 * bg: 'cyan.50'      // Light cyan (calm)
 *
 * Or use custom rgba:
 * bg: 'rgba(128, 90, 213, 0.1)'  // Purple with custom opacity
 */
export const TABLE_ROW_HOVER_STYLES = {
    bg: 'gray.100'
} as const;

/**
 * Table header cell (Th) base styles
 *
 * 📏 Padding options (current: px: 1 = 4px):
 * px: 0.5  // 2px - very tight
 * px: 1    // 4px - compact (current) ✓
 * px: 1.5  // 6px - comfortable
 * px: 2    // 8px - spacious (default)
 *
 * 📝 Font size: 'xs' = 12px (compact headers)
 */
export const TABLE_TH_BASE_STYLES = {
    borderColor: 'gray.300',
    px: 1,
    py: 1,
    fontSize: 'xs' as const
} as const;

/**
 * Table data cell (Td) base styles
 *
 * 📝 Font size: 'xs' = 12px (compact cells)
 */
export const TABLE_TD_BASE_STYLES = {
    borderColor: 'gray.300',
    px: 1,
    py: 1,
    fontSize: 'xs' as const
} as const;

/**
 * Table data cell with small font
 *
 * 📝 Font size options:
 * fontSize: 'xs'   // 12px - extra small (current) ✓
 * fontSize: 'sm'   // 14px - small (default)
 * fontSize: 'md'   // 16px - medium
 */
export const TABLE_TD_SMALL_STYLES = {
    ...TABLE_TD_BASE_STYLES,
    fontSize: 'xs' as const
} as const;

/**
 * Table data cell with center alignment
 */
export const TABLE_TD_CENTER_STYLES = {
    ...TABLE_TD_BASE_STYLES,
    textAlign: 'center' as const
} as const;

/**
 * Planet badge/text styles (for colored planet display)
 *
 * 📝 Font size options:
 * fontSize: 'xs'   // 12px - compact
 * fontSize: 'sm'   // 14px - default (current) ✓
 */
/*
 *  Основные значения:
  - 'normal' (400) - обычный
  - 'medium' (500) - средний
  - 'semibold' (600) - полужирный
  - 'bold' (700) - жирный

  Дополнительные:
  - 'hairline' (100) - сверхтонкий
  - 'thin' (200) - тонкий
  - 'light' (300) - легкий
  - 'extrabold' (800) - сверхжирный
  - 'black' (900) - максимально жирный

  Или числа: 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900

  Для планет обычно используют:
  - 'normal' (текущий) - для обычного текста
  - 'medium' - для большей читабельности
  - 'semibold' - для выделения


 */

export const PLANET_TEXT_STYLES = {
    fontSize: 'xs' as const,
    fontWeight: 500,
    px: 0.5,
    py: 0.5,
    borderRadius: 'sm',
    display: 'inline-block' as const,
    lineHeight: '1.2'
} as const;

/**
 * Empty message box styles
 */
export const EMPTY_MESSAGE_BOX_STYLES = {
    p: 4,
    textAlign: 'center' as const,
    color: 'gray.500'
} as const;

/**
 * Empty cell text styles (for rows with no data)
 */
export const EMPTY_CELL_TEXT_STYLES = {
    color: 'gray.400',
    fontSize: 'sm' as const
} as const;

/**
 * Alternative table wrapper for inline tables (NakshatraAngaInfoTable)
 * Note: Border moved to TableContainer for consistency with other tables
 */
export const TABLE_WRAPPER_INLINE_STYLES = {
    bg: 'white',
    borderRadius: 'md',
    overflow: 'hidden' as const,
    mt: 2
} as const;

/**
 * Helper function to create column width styles
 */
export function createColumnWidth(minW: string) {
    return {
        ...TABLE_TH_BASE_STYLES,
        minW
    } as const;
}

/**
 * Common column widths (preset configurations)
 */
export const COLUMN_WIDTHS = {
    xsmall: '30px',
    small: '40px',
    medium: '60px',
    large: '80px',
    xlarge: '100px',
    xxlarge: '120px',
    huge: '140px',
    massive: '200px',
    auto: 'auto'
};

/**
 * Pre-configured column styles by width
 */
export const COLUMN_STYLES = {
    xsmall: createColumnWidth(COLUMN_WIDTHS.xsmall),
    small: createColumnWidth(COLUMN_WIDTHS.small),
    medium: createColumnWidth(COLUMN_WIDTHS.medium),
    large: createColumnWidth(COLUMN_WIDTHS.large),
    xlarge: createColumnWidth(COLUMN_WIDTHS.xlarge),
    xxlarge: createColumnWidth(COLUMN_WIDTHS.xxlarge),
    huge: createColumnWidth(COLUMN_WIDTHS.huge),
    massive: createColumnWidth(COLUMN_WIDTHS.massive)
};

/**
 * Rainbow colors for Tara Phala (9 cyclic values)
 *
 * 🌈 Classic rainbow spectrum - each Tara gets its own color
 * 💡 Change any color to customize the palette
 * 🎨 Use 400-600 shades for vibrant text colors
 *
 * Examples of alternative palettes:
 * ─────────────────────────────────
 * Pastel (softer):     red.300, orange.300, yellow.300, green.300...
 * Vibrant (brighter):  red.600, orange.600, yellow.600, green.600...
 * Dark mode:           red.400, orange.400, yellow.400, green.400...
 */
export const TARA_PHALA_COLORS = [
    'red.500',      // 1. Janma - birth (red)
    'orange.500',   // 2. Sampat - success (orange)
    'yellow.500',   // 3. Vipat - failure (yellow)
    'green.500',    // 4. Kshema - comfort (green)
    'teal.500',     // 5. Pratipa - enmity (cyan/teal)
    'blue.500',     // 6. Sadhana - practice (blue)
    'purple.600',   // 7. Naidhana - destruction (indigo)
    'purple.400',   // 8. Mitra - friendship (violet)
    'pink.500'      // 9. Parama - important (pink)
];

/**
 * Rank colors for Chara Karaka (7 ranks from green to red)
 *
 * 🎯 Gradient from good (green) to challenging (red)
 * 💡 Represents distance from rashi center (15°)
 *
 * Color meaning:
 * Green  → Closest to center (strongest karaka)
 * Yellow → Medium distance
 * Orange → Far from center
 * Red    → Farthest (weakest karaka)
 */
export const RANK_COLORS = [
    'green.500',    // 1. Closest to center (best)
    'green.400',    // 2. Very close
    'yellow.500',   // 3. Medium distance
    'orange.400',   // 4. Getting far
    'orange.500',   // 5. Far
    'red.400',      // 6. Very far
    'red.500'       // 7. Farthest from center (worst)
];

/**
 * Get Tara Phala color by number (1-9)
 */
export function getTaraPhalaColor(taraNumber: number): string {
    if (taraNumber < 1 || taraNumber > 9) {
        return 'gray.500';
    }
    return TARA_PHALA_COLORS[taraNumber - 1];
}

/**
 * Get rank color for Chara Karaka (1-7)
 */
export function getRankColor(rank: number): string {
    if (rank < 1 || rank > 7) {
        return 'gray.500';
    }
    return RANK_COLORS[rank - 1];
}
